import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  CloudUpload,
  Trash2
} from 'lucide-react';

// Matches the same pattern used in ThemesAndLayout.js
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';

function getAssetBaseUrl(api) {
    if (ASSET_BASE_URL) return ASSET_BASE_URL;
    if (api?.defaults?.baseURL) return api.defaults.baseURL.replace(/\/api\/?$/, '');
    return '';
}

function buildImageUrl(path, api) {
    if (!path) return '';
    return `${getAssetBaseUrl(api)}${path}`;
}

const DEFAULT_PASS_JSON = JSON.stringify(
    {
        foregroundColor: 'rgb(255, 255, 255)',
        backgroundColor: 'rgb(0, 100, 60)',
        labelColor: 'rgb(200, 230, 210)'
    },
    null,
    2
);

function parseColors(passJson) {
    try {
        const obj = typeof passJson === 'string' ? JSON.parse(passJson) : passJson;
        return {
            foregroundColor: obj.foregroundColor || 'rgb(255, 255, 255)',
            backgroundColor: obj.backgroundColor || 'rgb(0, 100, 60)',
            labelColor: obj.labelColor || 'rgb(200, 230, 210)'
        };
    } catch {
        return {
            foregroundColor: 'rgb(255, 255, 255)',
            backgroundColor: 'rgb(0, 100, 60)',
            labelColor: 'rgb(200, 230, 210)'
        };
    }
}

// Convert "rgb(r, g, b)" → "#rrggbb" for <input type="color">
function rgbToHex(rgb) {
    if (!rgb) return '#000000';
    if (rgb.startsWith('#')) return rgb;
    const match = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!match) return '#000000';
    return (
        '#' +
        [match[1], match[2], match[3]]
            .map((n) => parseInt(n, 10).toString(16).padStart(2, '0'))
            .join('')
    );
}

// Convert "#rrggbb" → "rgb(r, g, b)"
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return hex;
    return `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`;
}

// ---------- Pass Card Preview ----------

function PassPreview({ logo, banner, colors, memberNumberPrefix, api }) {
    const { foregroundColor, backgroundColor, labelColor } = colors;
    const displayMemberNumber = `${memberNumberPrefix || 'MBR-'}00000001`;

    const logoSrc = logo ? buildImageUrl(logo, api) : null;
    const bannerSrc = banner ? buildImageUrl(banner, api) : null;

    return (
        <div
            className="rounded-2xl overflow-hidden shadow-2xl mx-auto"
            style={{
                width: 320,
                backgroundColor,
                fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif"
            }}
        >
            {/* Header bar */}
            <div
                className="flex items-center justify-between px-4 pt-4 pb-2"
                style={{ backgroundColor }}
            >
                {logoSrc ? (
                    <img src={logoSrc} alt="Logo" className="h-8 object-contain" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                ) : (
                    <div
                        className="h-8 w-24 rounded flex items-center justify-center text-xs font-bold"
                        style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: foregroundColor }}
                    >
                        LOGO
                    </div>
                )}
                <div className="text-right">
                    <div className="text-xs" style={{ color: labelColor }}>
                        MEMBER #
                    </div>
                    <div className="text-sm font-bold" style={{ color: foregroundColor }}>
                        {displayMemberNumber}
                    </div>
                </div>
            </div>

            {/* Banner / strip */}
            {bannerSrc ? (
                <img
                    src={bannerSrc}
                    alt="Banner"
                    className="w-full object-cover"
                    style={{ maxHeight: 100 }}
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                />
            ) : (
                <div
                    className="w-full flex items-center justify-center text-xs"
                    style={{
                        height: 80,
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        color: foregroundColor,
                        borderTop: `1px solid rgba(255,255,255,0.15)`,
                        borderBottom: `1px solid rgba(255,255,255,0.15)`
                    }}
                >
                    Banner Image (strip.png)
                </div>
            )}

            {/* Secondary + Auxiliary fields */}
            <div className="px-4 pb-3 flex items-center justify-between">
                <div className="text-left">
                    <div className="text-xs" style={{ color: labelColor }}>
                        NAME
                    </div>
                    <div className="text-sm font-medium" style={{ color: foregroundColor }}>
                        John Smith
                    </div>
                </div>
                <div className="ml-auto text-right">
                    <div className="text-xs" style={{ color: labelColor }}>
                        MEMBER SINCE
                    </div>
                    <div className="text-sm font-medium" style={{ color: foregroundColor }}>
                        01/01/2025
                    </div>
                </div>
            </div>

            {/* QR code placeholder (matches Apple Wallet pass) */}
            <div
                className="mx-4 mb-4 rounded-lg flex items-center justify-center bg-white p-2"
                style={{ minHeight: 48 }}
            >
                <svg
                    width={100}
                    height={100}
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="max-h-24 w-auto"
                    aria-hidden
                >
                    <rect width="30" height="30" fill="white" />
                    {/* Finder patterns */}
                    <rect x="0" y="0" width="8" height="8" fill="black" />
                    <rect x="2" y="2" width="4" height="4" fill="white" />
                    <rect x="22" y="0" width="8" height="8" fill="black" />
                    <rect x="24" y="2" width="4" height="4" fill="white" />
                    <rect x="0" y="22" width="8" height="8" fill="black" />
                    <rect x="2" y="24" width="4" height="4" fill="white" />
                    {/* Data modules - simple grid pattern */}
                    {[10, 12, 14, 16, 18, 20].flatMap((x) =>
                        [10, 12, 14, 16, 18, 20].map((y) =>
                            (x + y) % 2 === 0 ? <rect key={`${x}-${y}`} x={x} y={y} width="2" height="2" fill="black" /> : null
                        )
                    ).filter(Boolean)}
                </svg>
            </div>
        </div>
    );
}

// ---------- Image Upload Field ----------

function ImageUploadField({ label, hint, currentUrl, uploading, onUpload, onRemove, api }) {
    const inputRef = useRef(null);

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
            <div className="flex items-start space-x-3">
                {currentUrl ? (
                    <div className="relative flex-shrink-0">
                        <img
                            src={buildImageUrl(currentUrl, api)}
                            alt={label}
                            className="h-16 w-auto rounded-lg border border-gray-200 object-contain bg-gray-50"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                        />
                        <button
                            type="button"
                            onClick={onRemove}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                        >
                            <Trash2 className="h-3 w-3" />
                        </button>
                    </div>
                ) : (
                    <div className="h-16 w-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 flex-shrink-0">
                        <span className="text-xs text-gray-400 text-center px-1">No image</span>
                    </div>
                )}
                <div>
                    <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={inputRef}
                        onChange={(e) => {
                            if (e.target.files[0]) onUpload(e.target.files[0]);
                            e.target.value = '';
                        }}
                    />
                    <button
                        type="button"
                        disabled={uploading}
                        onClick={() => inputRef.current && inputRef.current.click()}
                        className="inline-flex items-center space-x-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        <CloudUpload className="h-4 w-4" />
                        <span>{uploading ? 'Uploading…' : 'Upload'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------- Main Component ----------

const MembershipConfig = ({
    campaignId,
    api,
    setSuccessMessage,
    setShowSuccess,
    setErrorMessage,
    setShowError
}) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingBanner, setUploadingBanner] = useState(false);

    // Local editable state
    const [passJson, setPassJson] = useState(DEFAULT_PASS_JSON);
    const [passJsonError, setPassJsonError] = useState('');
    const [colors, setColors] = useState(parseColors(DEFAULT_PASS_JSON));
    const [memberNumberPrefix, setMemberNumberPrefix] = useState('MBR-');

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/campaigns/${campaignId}/membership-settings`);
            if (data.settings) {
                setSettings(data.settings);
                const json = data.settings.pass_json || DEFAULT_PASS_JSON;
                setPassJson(typeof json === 'string' ? json : JSON.stringify(json, null, 2));
                setColors(parseColors(json));
                setMemberNumberPrefix(data.settings.member_number_prefix ?? 'MBR-');
            }
        } catch (err) {
            console.error('Fetch membership settings:', err);
        } finally {
            setLoading(false);
        }
    }, [api, campaignId]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // Keep colors in sync when pass_json textarea changes
    const handlePassJsonChange = (value) => {
        setPassJson(value);
        try {
            JSON.parse(value);
            setPassJsonError('');
            setColors(parseColors(value));
        } catch {
            setPassJsonError('Invalid JSON');
        }
    };

    const handleColorChange = (field, hexValue) => {
        const rgbValue = hexToRgb(hexValue);
        setColors((prev) => ({ ...prev, [field]: rgbValue }));
        // Sync back into pass_json
        setPassJson((prev) => {
            try {
                const obj = JSON.parse(prev);
                obj[field] = rgbValue;
                return JSON.stringify(obj, null, 2);
            } catch {
                return prev;
            }
        });
        setPassJsonError('');
    };

    const handleSave = async () => {
        if (passJsonError) return;
        setSaving(true);
        try {
            const { data } = await api.put(`/admin/campaigns/${campaignId}/membership-settings`, {
                pass_json: passJson,
                member_number_prefix: memberNumberPrefix
            });
            setSettings(data.settings);
            setSuccessMessage && setSuccessMessage('Membership settings saved.');
            setShowSuccess && setShowSuccess(true);
            setTimeout(() => setShowSuccess && setShowSuccess(false), 3000);
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to save membership settings.';
            setErrorMessage && setErrorMessage(msg);
            setShowError && setShowError(true);
            setTimeout(() => setShowError && setShowError(false), 3000);
        } finally {
            setSaving(false);
        }
    };

    const handleUpload = async (file, type) => {
        const setUploading = type === 'logo' ? setUploadingLogo : setUploadingBanner;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const { data } = await api.post(
                `/admin/campaigns/${campaignId}/membership-settings/upload?type=${type}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setSettings((prev) => ({ ...(prev || {}), [type]: data.url }));
            setSuccessMessage && setSuccessMessage(`${type === 'logo' ? 'Logo' : 'Banner'} uploaded.`);
            setShowSuccess && setShowSuccess(true);
            setTimeout(() => setShowSuccess && setShowSuccess(false), 3000);
        } catch (err) {
            const msg = err.response?.data?.error || `Failed to upload ${type}.`;
            setErrorMessage && setErrorMessage(msg);
            setShowError && setShowError(true);
            setTimeout(() => setShowError && setShowError(false), 3000);
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async (type) => {
        try {
            await api.delete(`/admin/campaigns/${campaignId}/membership-settings/${type}`);
            setSettings((prev) => ({ ...(prev || {}), [type]: null }));
            setSuccessMessage && setSuccessMessage(`${type === 'logo' ? 'Logo' : 'Banner'} removed.`);
            setShowSuccess && setShowSuccess(true);
            setTimeout(() => setShowSuccess && setShowSuccess(false), 3000);
        } catch (err) {
            const msg = err.response?.data?.error || `Failed to remove ${type}.`;
            setErrorMessage && setErrorMessage(msg);
            setShowError && setShowError(true);
            setTimeout(() => setShowError && setShowError(false), 3000);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900">Membership Card Settings</h3>
                <p className="text-sm text-gray-500 mt-1">
                    Customise the Apple Wallet membership card appearance for this campaign.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* ── Left: Settings ── */}
                <div className="space-y-6">
                    {/* Logo */}
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-800 mb-4">Images</h4>
                        <div className="space-y-5">
                            <ImageUploadField
                                label="Logo"
                                hint="Appears in the top-left of the pass. PNG recommended (160×50 px)."
                                currentUrl={settings?.logo || null}
                                uploading={uploadingLogo}
                                onUpload={(file) => handleUpload(file, 'logo')}
                                onRemove={() => handleRemove('logo')}
                                api={api}
                            />
                            <ImageUploadField
                                label="Banner / Strip"
                                hint="Displayed as the background strip image on the pass. PNG recommended (640×246 px)."
                                currentUrl={settings?.banner || null}
                                uploading={uploadingBanner}
                                onUpload={(file) => handleUpload(file, 'banner')}
                                onRemove={() => handleRemove('banner')}
                                api={api}
                            />
                        </div>
                    </div>

                    {/* Member Number Prefix */}
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-800 mb-1">Member Number</h4>
                        <p className="text-xs text-gray-500 mb-3">
                            Prefix shown before the padded number on the pass (e.g. <code>MBR-</code> → <span className="font-mono">MBR-00000001</span>, <code>GD</code> → <span className="font-mono">GD00000001</span>).
                        </p>
                        <div className="flex items-center space-x-2">
                            <input
                                type="text"
                                value={memberNumberPrefix}
                                onChange={(e) => setMemberNumberPrefix(e.target.value)}
                                maxLength={20}
                                placeholder="MBR-"
                                spellCheck={false}
                                className="w-32 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                            />
                            <span className="text-sm text-gray-500 font-mono">
                                → {memberNumberPrefix || 'MBR-'}00000001
                            </span>
                        </div>
                    </div>

                    {/* Colors */}
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-800 mb-4">Colors</h4>
                        <div className="space-y-4">
                            {[
                                { field: 'backgroundColor', label: 'Background Color' },
                                { field: 'foregroundColor', label: 'Foreground / Text Color' },
                                { field: 'labelColor', label: 'Label Color' }
                            ].map(({ field, label }) => (
                                <div key={field}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        {label}
                                    </label>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="color"
                                            value={rgbToHex(colors[field])}
                                            onChange={(e) => handleColorChange(field, e.target.value)}
                                            className="h-9 w-10 rounded border border-gray-300 cursor-pointer p-0.5 flex-shrink-0"
                                            title="Pick a color"
                                        />
                                        <input
                                            type="text"
                                            value={rgbToHex(colors[field])}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                // Accept partial input while typing; only apply when it's a valid hex
                                                if (/^#[0-9a-fA-F]{6}$/.test(val)) {
                                                    handleColorChange(field, val);
                                                }
                                            }}
                                            onBlur={(e) => {
                                                // On blur, normalise to 6-digit hex or revert
                                                let val = e.target.value.trim();
                                                if (!val.startsWith('#')) val = `#${val}`;
                                                if (/^#[0-9a-fA-F]{6}$/.test(val)) {
                                                    handleColorChange(field, val);
                                                }
                                            }}
                                            maxLength={7}
                                            placeholder="#000000"
                                            spellCheck={false}
                                            className="w-28 px-2 py-1.5 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                                        />
                                        <span className="text-xs text-gray-400 font-mono flex-1 truncate">
                                            {colors[field]}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* pass.json editor */}
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-800 mb-1">
                            Pass JSON (Advanced)
                        </h4>
                        <p className="text-xs text-gray-500 mb-3">
                            Edit the raw pass appearance JSON. Color pickers above stay in sync.
                        </p>
                        <textarea
                            value={passJson}
                            onChange={(e) => handlePassJsonChange(e.target.value)}
                            rows={10}
                            spellCheck={false}
                            className={`w-full font-mono text-xs border rounded-lg p-3 focus:outline-none focus:ring-2 ${
                                passJsonError
                                    ? 'border-red-400 focus:ring-red-300'
                                    : 'border-gray-300 focus:ring-teal-400'
                            }`}
                        />
                        {passJsonError && (
                            <p className="text-xs text-red-500 mt-1">{passJsonError}</p>
                        )}
                    </div>

                    {/* Save */}
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || !!passJsonError}
                        className="w-full py-3 px-6 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 disabled:opacity-50 transition-colors"
                    >
                        {saving ? 'Saving…' : 'Save Settings'}
                    </button>
                </div>

                {/* ── Right: Preview ── */}
                <div className="flex flex-col items-center">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 self-start">
                        Live Preview
                    </h4>
                    <div className="sticky top-6">
                        <PassPreview
                            logo={settings?.logo || null}
                            banner={settings?.banner || null}
                            colors={colors}
                            memberNumberPrefix={memberNumberPrefix}
                            api={api}
                        />
                        <p className="text-xs text-gray-400 text-center mt-3">
                            Apple Wallet store card — approximate preview
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MembershipConfig;
